import { ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import type React from 'react';
import type { ReactNode } from 'react';

type ModalV2HeaderProps = {
    onClose?: () => void;
    goBack?: () => void;
    children?: ReactNode;
};

const ModalV2Header: React.FC<ModalV2HeaderProps> = ({ onClose, goBack, children }) => {
    const hasButtons = Boolean(goBack || onClose);
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    return (
        <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            borderBottom={(t) => `1px solid ${t.palette.divider}`}
        >
            {((!isSmOrLower && hasButtons) || (isSmOrLower && goBack)) && (
                <IconButton
                    size={'large'}
                    onClick={goBack}
                    sx={{
                        visibility: goBack ? 'visible' : 'hidden',
                        m: { xs: 0.5, sm: 1 },
                        p: { xs: 1, sm: 2 },
                    }}
                >
                    <ArrowBack fontSize='large' />
                </IconButton>
            )}

            <Typography
                color='primary'
                variant={isSmOrLower ? 'subtitle1' : 'h6'}
                p={2}
                sx={{
                    textAlign: { xs: 'left', md: 'center' },
                    // With a back arrow, the header has 3 flex children — `space-between`
                    // centers this box's own (shrink-to-content) width between them, which
                    // defeats `textAlign: left` on mobile. Stretching it to fill the gap
                    // between the two icons lets the text-align actually take effect.
                    ...(goBack && { flex: 1 }),
                }}
            >
                {children}
            </Typography>

            {hasButtons && (
                <IconButton
                    size={'large'}
                    onClick={onClose}
                    sx={{
                        visibility: onClose ? 'visible' : 'hidden',
                        m: { xs: 0.5, sm: 1 },
                        p: { xs: 1, sm: 2 },
                    }}
                >
                    <Close fontSize='large' />
                </IconButton>
            )}
        </Stack>
    );
};

export default ModalV2Header;
